import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X as XIcon, Info } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';

function computeRescheduleDate(): string {
  const date = new Date();
  date.setDate(date.getDate() + 60);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

// -- Animations --

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

// -- Styled Components --

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: ${fadeIn} 0.2s ease-out;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow-y: auto;
  animation: ${slideUp} 0.3s ease-out;

  @media (min-width: 768px) {
    border-radius: 16px;
  }
`;

const SheetContent = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const TitleText = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const BodyText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #292929;
  margin: 0;
`;

const InfoBox = styled.div`
  background: #d1d1d1;
  border-radius: 6px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: #292929;
`;

const InfoText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: #292929;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 0;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PauseButton = styled.button`
  width: 100%;
  padding: 14px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: #292929;
  border: none;
  color: #fff;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelTextButton = styled.button`
  background: none;
  border: none;
  padding: 10px 32px;
  font-size: 16px;
  font-weight: 400;
  color: #292929;
  cursor: pointer;
  text-decoration: underline;
  transition: opacity 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// -- Component --

interface CancelMembershipPageProps {
  readonly subscription: CustomerSubscription;
  readonly isLoading: boolean;
  readonly onCancelIntent: () => void;
  readonly onPause: () => void;
  readonly onBack: () => void;
}

export function CancelMembershipPage({
  subscription: _subscription,
  isLoading,
  onCancelIntent,
  onPause,
  onBack,
}: CancelMembershipPageProps) {
  const rescheduleDate = computeRescheduleDate();

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onBack();
    }
  };

  return (
    <Backdrop onClick={handleBackdropClick} data-testid="cancel-backdrop">
      <Sheet role="dialog" aria-modal="true" aria-label="Cancel subscription">
        <SheetContent>
          <TitleRow>
            <TitleText>Would you like to pause instead?</TitleText>
            <CloseButton
              onClick={onBack}
              disabled={isLoading}
              aria-label="Close"
            >
              <XIcon size={22} />
            </CloseButton>
          </TitleRow>

          <BodyText>
            Canceling will stop your subscription. By pausing, you'll skip this
            shipment and your next order will be automatically scheduled in 60
            days.
          </BodyText>

          <InfoBox>
            <InfoIcon>
              <Info size={18} />
            </InfoIcon>
            <InfoText>
              Your subscription will be rescheduled for {rescheduleDate}
            </InfoText>
          </InfoBox>

          <Divider />

          <CTASection>
            <PauseButton
              type="button"
              onClick={onPause}
              disabled={isLoading}
            >
              {isLoading ? 'Pausing...' : 'Pause subscription'}
            </PauseButton>
            <CancelTextButton
              type="button"
              onClick={onCancelIntent}
              disabled={isLoading}
            >
              Cancel subscription
            </CancelTextButton>
          </CTASection>
        </SheetContent>
      </Sheet>
    </Backdrop>
  );
}
